import { useState, useEffect } from 'react';
import { Table, Input, Row, Col, Badge, Spinner } from 'reactstrap';
import { RefreshCw, Eye, FileText } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn, H5 } from '../../../AbstractElements';
import CaseFollowUpService from '../../../Api/services/case-follow-up.service';
import { useAuth } from '../../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import type { components } from '../../../Api/types/openapi';
import { useNavigate } from 'react-router-dom';

// Tipos del OpenAPI
type CaseFollowUpUSCISResponse = components['schemas']['app__core__serializer__case_follow_up_lawyer__CaseFollowUpUSCISResponse'];
type CaseFollowUpWithLawyerResponse = components['schemas']['CaseFollowUpWithLawyerResponse'];
type CaseFollowUpResponse = components['schemas']['app__core__serializer__case_follow_up__CaseFollowUpResponse'];
type CaseFollowUpAdminBase = components['schemas']['CaseFollowUpAdminBase'];
type CaseFollowUpRelationShipResponse = components['schemas']['CaseFollowUpRelationShipClientAndLawyersResponse'];
type PaginationCaseFollowUpUSCIS = components['schemas']['app__common__serializer__pagination__Pagination[CaseFollowUpUSCISResponse]'];
type PaginationCaseFollowUpWithLawyer = components['schemas']['Pagination_CaseFollowUpWithLawyerResponse_'];
type PaginationCaseFollowUp = components['schemas']['Pagination_CaseFollowUpResponse_'];
type PaginationCaseFollowUpAdmin = components['schemas']['Pagination_CaseFollowUpAdminBase_'];
type PaginationCaseFollowUpRelationShip = components['schemas']['Pagination_CaseFollowUpRelationShipClientAndLawyersResponse_'];

type CaseData = CaseFollowUpUSCISResponse | CaseFollowUpWithLawyerResponse | CaseFollowUpResponse | CaseFollowUpAdminBase | CaseFollowUpRelationShipResponse;
type PaginationData = PaginationCaseFollowUpUSCIS | PaginationCaseFollowUpWithLawyer | PaginationCaseFollowUp | PaginationCaseFollowUpAdmin | PaginationCaseFollowUpRelationShip;

interface CaseFollowUpListProps {
  viewType?: 'active' | 'all' | 'all-relations' | 'admin-all' | 'admin-lawyers' | 'admin-clients';
  onCaseClick?: (caseNumber: string) => void;
}

const CaseFollowUpList: React.FC<CaseFollowUpListProps> = ({ 
  viewType = 'all',
  onCaseClick 
}) => {
  const { hasRole } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [cases, setCases] = useState<CaseData[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  
  // Filtros
  const [caseTypeFilter, setCaseTypeFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCases();
  }, [currentPage, caseTypeFilter, viewType]);

  const loadCases = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit,
      };

      if (caseTypeFilter) params.case_type = caseTypeFilter;
      if (searchTerm && viewType === 'admin-all') params.case_number = searchTerm;

      let data: PaginationData;
      
      if (hasRole('admin')) {
        if (viewType === 'admin-all') {
          data = await CaseFollowUpService.getAdminAllCases(params);
        } else if (viewType === 'admin-lawyers') {
          data = await CaseFollowUpService.getAdminLawyerCases(params);
        } else if (viewType === 'admin-clients') {
          data = await CaseFollowUpService.getAdminClientCases(params);
        } else {
          data = await CaseFollowUpService.getAdminAllCases(params);
        }
      } else if (hasRole('lawyer')) {
        if (viewType === 'active') {
          data = await CaseFollowUpService.getLawyerActiveCases(params);
        } else if (viewType === 'all-relations') {
          data = await CaseFollowUpService.getLawyerAllCases(params);
        } else {
          data = await CaseFollowUpService.getLawyerUSCISCases(params);
        }
      } else {
        return;
      }

      const results = (data as any).results;
      if (!Array.isArray(results)) {
        console.warn('La respuesta no contiene un array de resultados válido', data);
        setCases([]);
        setPagination(data);
        return;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 CaseFollowUpList - Datos cargados:', {
          viewType,
          totalResults: results.length,
          firstResult: results[0],
          pagination: {
            current_page: (data as any).current_page,
            pages: (data as any).pages,
            for_page: (data as any).for_page,
          }
        });
      }
      
      setCases(results);
      setPagination(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar casos');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getCaseTypeBadge = (caseType: string | null | undefined) => {
    if (!caseType) return <Badge color="secondary">-</Badge>;
    
    switch (caseType) {
      case 'USCIS':
        return <Badge color="primary">USCIS</Badge>;
      case 'EOIR':
        return <Badge color="info">EOIR</Badge>;
      case 'CEAC':
        return <Badge color="success">CEAC</Badge>;
      default:
        return <Badge color="secondary">{caseType}</Badge>;
    }
  };

  const getCaseNumber = (caseData: CaseData): string => {
    // Intentar obtener el número de caso desde diferentes estructuras
    if ('case_follow_up_base' in caseData && caseData.case_follow_up_base?.case_number_uscis) {
      return caseData.case_follow_up_base.case_number_uscis;
    }
    if ('case_number_uscis' in caseData && caseData.case_number_uscis) {
      return caseData.case_number_uscis;
    }
    return '-';
  };

  const getCaseName = (caseData: CaseData): string => {
    // Para CaseFollowUpAdminBase, no hay name directo, usar número de caso como nombre
    if ('cases_lawyers' in caseData && 'cases_clients' in caseData) {
      // Es CaseFollowUpAdminBase, usar número de caso como nombre
      const caseNumber = getCaseNumber(caseData);
      return caseNumber !== '-' ? `Caso ${caseNumber}` : 'Caso sin número';
    }
    // Para otros tipos, buscar name directamente
    if ('name' in caseData && caseData.name) {
      return caseData.name;
    }
    return '-';
  };

  const getLastUpdate = (caseData: CaseData): string => {
    if ('updated_at' in caseData && caseData.updated_at) {
      return new Date(caseData.updated_at).toLocaleDateString('es-ES');
    }
    if ('created_at' in caseData && caseData.created_at) {
      return new Date(caseData.created_at).toLocaleDateString('es-ES');
    }
    return '-';
  };

  const handleCaseClick = (caseData: CaseData) => {
    const caseNumber = getCaseNumber(caseData);
    if (caseNumber && caseNumber !== '-') {
      if (onCaseClick) {
        onCaseClick(caseNumber);
      } else {
        navigate(`/app/case-follow-up/detail/${caseNumber}`);
      }
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
    loadCases();
  };

  const renderPagination = () => {
    if (!pagination || (pagination as any).pages <= 1) return null;

    const pages = [];
    const totalPages = (pagination as any).pages;
    
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Btn
          key={i}
          color={i === currentPage ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setCurrentPage(i)}
          className="me-1"
        >
          {i}
        </Btn>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          <span className="text-muted">
            {t('Página')} {(pagination as any).current_page} {t('de')} {totalPages} 
            ({(pagination as any).results?.length || 0} {t('resultados') || 'resultados'})
          </span>
        </div>
        <div>{pages}</div>
      </div>
    );
  };

  if (loading && cases.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div>
      {/* Filtros y Búsqueda */}
      <Row className="mb-3">
        <Col md="4">
          <Input
            type="text"
            placeholder={t('Número de Caso USCIS')}
            value={searchTerm}
            onChange={handleSearch}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleFilterChange();
              }
            }}
            disabled={viewType !== 'admin-all'}
          />
        </Col>
        <Col md="3">
          <Input
            type="select"
            value={caseTypeFilter}
            onChange={(e) => {
              setCaseTypeFilter(e.target.value);
              handleFilterChange();
            }}
          >
            <option value="">{t('Todos los tipos') || 'Todos los tipos'}</option>
            <option value="USCIS">{t('USCIS')}</option>
            <option value="EOIR">{t('EOIR')}</option>
            <option value="CEAC">{t('CEAC')}</option>
          </Input>
        </Col>
        <Col md="3" className="text-end">
          <Btn color="secondary" onClick={loadCases} className="me-2">
            <RefreshCw size={16} className="me-1" />
            {t('Actualizar')}
          </Btn>
        </Col>
      </Row>

      {/* Tabla de Casos */}
      <div className="table-responsive theme-scrollbar">
        <Table hover responsive>
          <thead>
            <tr>
              <th>{t('Alias/Nombre')}</th>
              <th>{t('Número de Caso USCIS')}</th>
              <th>{t('Tipo de Caso')}</th>
              {hasRole('admin') && <th>{t('Cliente')}</th>}
              {hasRole('admin') && <th>{t('Abogado')}</th>}
              <th>{t('Última Actualización')}</th>
              <th>{t('Acciones')}</th>
            </tr>
          </thead>
          <tbody>
            {cases.length === 0 ? (
              <tr>
                <td colSpan={hasRole('admin') ? 7 : 5} className="text-center">
                  {t('No se encontraron casos')}
                </td>
              </tr>
            ) : (
              cases.map((caseData, index) => {
                const caseNumber = getCaseNumber(caseData);
                const caseName = getCaseName(caseData);
                const lastUpdate = getLastUpdate(caseData);
                const caseType = 'case_follow_up_base' in caseData 
                  ? caseData.case_follow_up_base?.case_type 
                  : 'case_type' in caseData 
                    ? (caseData as any).case_type 
                    : null;

                // Para admin-all (CaseFollowUpAdminBase), mostrar información de los arrays
                const isAdminBase = 'cases_lawyers' in caseData && 'cases_clients' in caseData;
                
                // Obtener clientes (para admin-all o admin-clients)
                const getClients = () => {
                  if (isAdminBase && Array.isArray((caseData as any).cases_clients) && (caseData as any).cases_clients.length > 0) {
                    return (caseData as any).cases_clients.map((c: any) => c.client?.name || c.client?.email || '-').join(', ');
                  }
                  if ('client' in caseData && (caseData as any).client) {
                    return (caseData as any).client.name || (caseData as any).client.email || '-';
                  }
                  return '-';
                };

                // Obtener abogados (para admin-all o admin-lawyers)
                const getLawyers = () => {
                  if (isAdminBase && Array.isArray((caseData as any).cases_lawyers) && (caseData as any).cases_lawyers.length > 0) {
                    return (caseData as any).cases_lawyers.map((l: any) => l.lawyer?.name || l.lawyer?.email || '-').join(', ');
                  }
                  if ('lawyer' in caseData && (caseData as any).lawyer) {
                    return (caseData as any).lawyer.name || (caseData as any).lawyer.email || '-';
                  }
                  return '-';
                };

                return (
                  <tr key={caseData.id || index}>
                    <td>{caseName}</td>
                    <td>{caseNumber}</td>
                    <td>{getCaseTypeBadge(caseType)}</td>
                    {hasRole('admin') && (
                      <td>
                        {getClients()}
                      </td>
                    )}
                    {hasRole('admin') && (
                      <td>
                        {getLawyers()}
                      </td>
                    )}
                    <td>{lastUpdate}</td>
                    <td>
                      <Btn
                        color="primary"
                        size="sm"
                        className="btn_list_action"
                        onClick={() => handleCaseClick(caseData)}
                        title={t('Ver Detalle')}
                        disabled={!caseNumber || caseNumber === '-'}
                      >
                        <Eye size={14} />
                      </Btn>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
      {renderPagination()}
    </div>
  );
};

export default CaseFollowUpList;

